import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Pressable, StyleSheet, View } from 'react-native';
import { BodyText, DisplayText, Icon, theme, useResponsive } from '@sg/garnish';

import { GiftCardRedemptionForm } from '../GiftCardRedemptionForm';

export const GiftCardRedemption = (props: GiftCardRedemptionProps) => {
  const { giftCardCode, giftCardRegistrationCode, onNavigateToForm } = props;

  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();

  if (currentBreakpoint.isXS) {
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={formatMessage(messages.a11yLabel)}
        style={styles.containerXS}
        onPress={onNavigateToForm}
      >
        <View style={styles.contentXS}>
          <Icon name="IconCreditCard" width={24} height={24} />

          <BodyText sizeMatch={['14']}>
            <FormattedMessage {...messages.title} />
          </BodyText>
        </View>

        <Icon name="IconCaretRight" width={24} height={24} />
      </Pressable>
    );
  }

  return (
    <View style={styles.containerSM}>
      <DisplayText sizeMatch={['32']}>
        <FormattedMessage {...messages.title} />
      </DisplayText>

      <GiftCardRedemptionForm
        giftCardCode={giftCardCode}
        giftCardRegistrationCode={giftCardRegistrationCode}
      />
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  containerXS: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: theme.spacing['2'],
  },
  containerSM: {
    gap: theme.spacing['4'],
    marginTop: theme.spacing['6'],
  },
  contentXS: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: theme.spacing['4'],
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Redeem gift card',
    description: 'Gift Card Redemption | Title',
  },
  a11yLabel: {
    defaultMessage: 'Open gift card redemption screen',
    description: 'Gift Card Redemption | A11y label',
  },
  giftCardNumber: {
    defaultMessage: 'Gift card number',
    description: 'Gift Card Redemption | Gift card number',
  },
  giftCardNumberPlaceholder: {
    defaultMessage: 'Enter number',
    description: 'Gift Card Redemption | Gift card number placeholder',
  },
  registrationCode: {
    defaultMessage: 'Registration code',
    description: 'Gift Card Redemption | Registration code',
  },
  registrationCodePlaceholder: {
    defaultMessage: 'Enter code',
    description: 'Gift Card Redemption | Registration code placeholder',
  },
  ctaLabel: {
    defaultMessage: 'Apply',
    description: 'Gift Card Redemption | CTA label',
  },
  ctaA11yLabel: {
    defaultMessage: 'Apply gift card',
    description: 'Gift Card Redemption | CTA A11y label',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type GiftCardRedemptionProps = {
  giftCardCode?: string;
  giftCardRegistrationCode?: string;
  onNavigateToForm: () => void;
};
